import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Icon, type IconName } from '../components/Icon.tsx';
import { Shell } from '../components/Shell.tsx';
import { SignatureView, type Strokes } from '../components/SignaturePad.tsx';
import { Button, Confirm, Dialog, EmptyState, Field, IconButton, Skeleton, StatusBadge, Switch, type Tone } from '../components/ui.tsx';
import { t, tn, type MessageKey } from '../i18n/index.ts';
import { action, ApiError, upload } from '../lib/api.ts';
import { dateTime, day as dayText, todayIso } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { useUi } from '../lib/ui-state.tsx';
import { useAction, useSave } from './manage/common.tsx';
import { Table } from './Reports.tsx';

/**
 * Documents: the business's files in folders (some for managers only), with older versions,
 * tags, notes, links to share a file with someone outside, and a trash. Signatures: a PDF sent to
 * one or more people, who each sign through their own link; when all have signed, the signed
 * copy and its certificate are filed in "Signed documents".
 */

type Kind = 'pdf' | 'image' | 'document' | 'sheet' | 'slides' | 'text' | 'archive';
type View = 'folder' | 'recent' | 'shared' | 'trash' | 'search';
type SignState = 'draft' | 'sent' | 'completed' | 'declined' | 'cancelled' | 'expired';
type SignerState = 'waiting' | 'ready' | 'viewed' | 'signed' | 'declined';

interface FolderRow {
  id: string;
  name: string;
  parent: string | null;
  access: 'everyone' | 'managers';
  description: string | null;
  files: number;
  subfolders: number;
}
interface FileRow {
  id: string;
  folder: string | null;
  folder_name: string | null;
  name: string;
  mime: string;
  kind: Kind;
  size: number;
  version: number;
  tags: string[];
  note: string | null;
  uploaded_by_name: string | null;
  source: 'upload' | 'sign';
  trashed_at: string | null;
  updated_at: string;
  created_at: string;
  shared: boolean;
}
interface Browse {
  folders: FolderRow[];
  unfiled: number;
  path: { id: string; name: string }[];
  files: FileRow[];
  tags: string[];
  totals: { files: number; bytes: number; trash: number; shared: number };
  can_manage: boolean;
  can_sign: boolean;
}
interface FileDetail extends FileRow {
  sha256: string;
  path: string[];
  trashed_by_name: string | null;
  may_change: boolean;
  versions: { id: string; version: number; name: string; size: number; sha256: string; by: string | null; at: string }[];
  shares: { id: string; token: string; expires: string | null; expired: boolean; downloads: number; last_download_at: string | null; by: string | null; at: string }[];
  activity: { kind: string; text: string | null; by: string | null; at: string }[];
  signatures: { id: string; name: string; title: string; state: SignState }[];
  can_manage: boolean;
  can_sign: boolean;
}
interface RequestRow {
  id: string;
  name: string;
  title: string;
  state: SignState;
  doc_name: string | null;
  signers: { name: string; state: SignerState }[];
  signed: number;
  expires: string | null;
  by: string | null;
  sent_at: string | null;
  completed_at: string | null;
  created_at: string;
}
interface RequestDetail {
  id: string;
  name: string;
  title: string;
  message: string | null;
  state: SignState;
  ordered: boolean;
  expires: string | null;
  file: string | null;
  file_name: string | null;
  doc_name: string | null;
  doc_sha256: string | null;
  doc_size: number | null;
  signed_file: string | null;
  certificate_file: string | null;
  closed_reason: string | null;
  by: string | null;
  sent_at: string | null;
  completed_at: string | null;
  created_at: string;
  signers: {
    id: string;
    position: number;
    name: string;
    email: string | null;
    phone: string | null;
    state: SignerState;
    token: string | null;
    viewed_at: string | null;
    signed_at: string | null;
    signed_name: string | null;
    signature: Strokes | null;
    ip: string | null;
    decline_reason: string | null;
  }[];
  events: { kind: string; at: string; who: string | null; ip: string | null; text: string | null }[];
  may_change: boolean;
}

const KIND_ICON: Record<Kind, IconName> = { pdf: 'file', image: 'image', document: 'file', sheet: 'chart', slides: 'layers', text: 'note', archive: 'box' };
const SIGN_TONE: Record<SignState, { tone: Tone; icon: IconName }> = {
  draft: { tone: 'neutral', icon: 'edit' },
  sent: { tone: 'info', icon: 'clock' },
  completed: { tone: 'ok', icon: 'checkCircle' },
  declined: { tone: 'danger', icon: 'x' },
  cancelled: { tone: 'neutral', icon: 'x' },
  expired: { tone: 'warn', icon: 'alert' },
};
const SIGNER_TONE: Record<SignerState, { tone: Tone; icon: IconName }> = {
  waiting: { tone: 'neutral', icon: 'clock' },
  ready: { tone: 'info', icon: 'send' },
  viewed: { tone: 'info', icon: 'eye' },
  signed: { tone: 'ok', icon: 'check' },
  declined: { tone: 'danger', icon: 'x' },
};
const MAX_FILE = 8_000_000;

const size = (n: number) => (n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)} MB` : n >= 1000 ? `${Math.round(n / 1000)} KB` : `${n} B`);
const readBase64 = (f: File) =>
  new Promise<string>((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).replace(/^data:[^,]*,/, ''));
    r.onerror = () => reject(r.error);
    r.readAsDataURL(f);
  });
const toBlob = (f: { mime: string; data: string }) => new Blob([Uint8Array.from(atob(f.data), (c) => c.charCodeAt(0))], { type: f.mime });
function saveFile(f: { name: string; mime: string; data: string }) {
  const url = URL.createObjectURL(toBlob(f));
  const a = document.createElement('a');
  a.href = url;
  a.download = f.name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
async function copy(text: string) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}
const activityText = (a: { kind: string; text: string | null }) => t(`docs.act_${a.kind}` as MessageKey, { text: a.text ?? '' });

function FileIcon({ kind, size: px = 20 }: { kind: Kind; size?: number }) {
  return (
    <span className={`doc-icon doc-${kind}`} aria-hidden="true">
      <Icon name={KIND_ICON[kind]} size={px} />
    </span>
  );
}

export function Documents() {
  const [params, setParams] = useSearchParams();
  const { can } = useSession();
  const tab = params.get('tab') === 'sign' && can('sign.send') ? 'sign' : 'files';
  const { data: counts, reload: reloadCounts } = useAction<{ counts: Record<string, number> } | null>(
    can('sign.send') ? 'sign.requests' : 'doc.browse',
    can('sign.send') ? { filter: 'waiting' } : { view: 'recent' },
  );
  const waiting = can('sign.send') ? (counts?.counts?.waiting ?? 0) : 0;
  const [newRequest, setNewRequest] = useState<{ file?: string } | null>(null);
  const [version, setVersion] = useState(0);
  const go = (k: 'files' | 'sign') => {
    const p = new URLSearchParams(params);
    if (k === 'sign') p.set('tab', 'sign');
    else p.delete('tab');
    setParams(p, { replace: true });
  };
  return (
    <Shell>
      <div className="page reports docs-page">
        <div className="page-head">
          <div>
            <h1>{t('docs.title')}</h1>
            <p className="muted">{t('docs.subtitle')}</p>
          </div>
          {can('sign.send') ? (
            <div className="page-actions">
              <Button variant={tab === 'sign' ? 'primary' : 'secondary'} icon="pen" onClick={() => setNewRequest({})}>
                {t('docs.new_request')}
              </Button>
            </div>
          ) : null}
        </div>
        {can('sign.send') ? (
          <div className="segmented tabs" role="tablist" aria-label={t('docs.sections')}>
            <button role="tab" aria-selected={tab === 'files'} className={tab === 'files' ? 'active' : ''} onClick={() => go('files')}>
              <Icon name="folder" size={16} />
              {t('docs.tab_files')}
            </button>
            <button role="tab" aria-selected={tab === 'sign'} className={tab === 'sign' ? 'active' : ''} onClick={() => go('sign')}>
              <Icon name="pen" size={16} />
              {t('docs.tab_sign')}
              {waiting ? <span className="hr-tab-badge">{waiting}</span> : null}
            </button>
          </div>
        ) : null}
        {tab === 'files' ? (
          <FilesTab key={version} onSign={(file) => setNewRequest({ file })} />
        ) : (
          <SignTab
            key={version}
            onNew={() => setNewRequest({})}
            onChange={async () => {
              await reloadCounts();
            }}
          />
        )}
      </div>
      {newRequest ? (
        <RequestForm
          file={newRequest.file ?? null}
          onClose={() => setNewRequest(null)}
          onSaved={async () => {
            setNewRequest(null);
            setVersion((v) => v + 1);
            await reloadCounts();
            if (tab !== 'sign') go('sign');
          }}
        />
      ) : null}
    </Shell>
  );
}

// ---- files ---------------------------------------------------------------------------------

interface Pending {
  file: File;
  existing: string;
}

function FilesTab({ onSign }: { onSign: (file: string) => void }) {
  const [params, setParams] = useSearchParams();
  const { toast, fail } = useUi();
  const view = (['recent', 'shared', 'trash'].includes(params.get('view') ?? '') ? params.get('view') : 'folder') as View;
  const folder = view === 'folder' ? params.get('folder') : null;
  const [q, setQ] = useState('');
  const [tag, setTag] = useState<string | null>(null);
  const searching = q.trim().length > 0 || !!tag;
  const input = { view: searching && view === 'folder' ? 'search' : view, folder, q: q.trim() || undefined, tag: tag ?? undefined };
  const { data, reload } = useAction<Browse>('doc.browse', input);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [open, setOpen] = useState<string | null>(params.get('file'));
  const [folderForm, setFolderForm] = useState<{ id?: string; parent: string | null } | null>(null);
  const [moving, setMoving] = useState<string[] | null>(null);
  const [confirm, setConfirm] = useState<'trash' | 'delete' | 'empty' | 'remove_folder' | null>(null);
  const [drag, setDrag] = useState(false);
  const [uploading, setUploading] = useState<{ done: number; total: number } | null>(null);
  const [asking, setAsking] = useState<Pending | null>(null);
  const queue = useRef<File[]>([]);
  const picker = useRef<HTMLInputElement>(null);
  const [sideOpen, setSideOpen] = useState(false);
  const { busy, run } = useSave();

  useEffect(() => setSelected(new Set()), [view, folder, q, tag]);
  const nav = (next: { view?: View; folder?: string | null }) => {
    const p = new URLSearchParams();
    if (params.get('tab')) p.set('tab', params.get('tab')!);
    if (next.view && next.view !== 'folder') p.set('view', next.view);
    if (next.folder) p.set('folder', next.folder);
    setParams(p, { replace: false });
    setQ('');
    setTag(null);
    setSideOpen(false);
  };
  const current = data?.folders.find((f) => f.id === folder) ?? null;
  const children = useMemo(() => (data ? data.folders.filter((f) => f.parent === (view === 'folder' ? folder : '__none')) : []), [data, folder, view]);
  const inTrash = view === 'trash';

  // Uploading: one file after the other; a name already in the folder asks what to do.
  const sendOne = async (f: File, ifExists: 'ask' | 'version' | 'copy') => {
    if (f.size > MAX_FILE) throw new ApiError(400, 'bad_request', t('docs.too_big', { name: f.name }));
    return upload<{ id: string; name: string; replaced: boolean }>('doc.upload', { folder, name: f.name, data: await readBase64(f), if_exists: ifExists });
  };
  const pump = async () => {
    while (queue.current.length) {
      const f = queue.current[0]!;
      try {
        await sendOne(f, 'ask');
        queue.current.shift();
        setUploading((u) => (u ? { ...u, done: u.done + 1 } : u));
      } catch (e) {
        if (e instanceof ApiError && e.details?.code === 'exists') {
          setAsking({ file: f, existing: String(e.details.id) });
          return;
        }
        queue.current.shift();
        fail(e);
      }
    }
    setUploading((u) => {
      if (u && u.done) toast(tn('docs.uploaded', u.done));
      return null;
    });
    await reload();
  };
  const add = (list: FileList | File[]) => {
    const files = [...list];
    if (!files.length) return;
    queue.current.push(...files);
    setUploading((u) => ({ done: u?.done ?? 0, total: (u?.total ?? 0) + files.length }));
    if (queue.current.length === files.length) void pump();
  };
  const resolve = async (choice: 'version' | 'copy' | 'skip') => {
    const p = asking!;
    setAsking(null);
    queue.current.shift();
    if (choice !== 'skip') {
      try {
        await sendOne(p.file, choice);
        setUploading((u) => (u ? { ...u, done: u.done + 1 } : u));
      } catch (e) {
        fail(e);
      }
    }
    await pump();
  };

  const canDrop = !inTrash && view === 'folder' && !searching;
  const ids = [...selected];
  const allChecked = !!data?.files.length && data.files.every((f) => selected.has(f.id));
  const crumbs = data?.path ?? [];
  const title = view === 'recent' ? t('docs.v_recent') : view === 'shared' ? t('docs.v_shared') : inTrash ? t('docs.v_trash') : (current?.name ?? t('docs.unfiled'));

  return (
    <div className={`docs-layout${sideOpen ? ' side-open' : ''}`}>
      <nav className="panel docs-side" aria-label={t('docs.folders')}>
        <button className="docs-side-toggle" aria-expanded={sideOpen} onClick={() => setSideOpen((o) => !o)}>
          <Icon name={inTrash ? 'trash' : view === 'folder' ? 'folder' : view === 'shared' ? 'share' : 'clock'} size={18} />
          <span>{title}</span>
          <Icon name={sideOpen ? 'up' : 'down'} size={16} />
        </button>
        <div className="docs-side-body">
          <ul className="docs-views">
            {(
              [
                ['recent', 'clock', t('docs.v_recent'), null],
                ['shared', 'share', t('docs.v_shared'), data?.totals.shared ?? null],
                ['trash', 'trash', t('docs.v_trash'), data?.totals.trash ?? null],
              ] as const
            ).map(([v, icon, label, n]) => (
              <li key={v}>
                <button className={view === v ? 'active' : ''} aria-current={view === v ? 'page' : undefined} onClick={() => nav({ view: v })}>
                  <Icon name={icon} size={16} />
                  <span>{label}</span>
                  {n ? <span className="docs-count">{n}</span> : null}
                </button>
              </li>
            ))}
          </ul>
          <div className="docs-side-head">
            <span className="field-label">{t('docs.folders')}</span>
            {data?.can_manage ? <IconButton icon="plus" label={t('docs.new_folder')} onClick={() => setFolderForm({ parent: null })} /> : null}
          </div>
          {!data ? (
            <Skeleton h={160} />
          ) : (
            <ul className="docs-tree" role="tree">
              <li role="treeitem" aria-selected={view === 'folder' && !folder}>
                <button className={view === 'folder' && !folder ? 'active' : ''} onClick={() => nav({ folder: null })}>
                  <Icon name="folder" size={16} />
                  <span>{t('docs.unfiled')}</span>
                  {data.unfiled ? <span className="docs-count">{data.unfiled}</span> : null}
                </button>
              </li>
              <Tree folders={data.folders} parent={null} depth={0} active={view === 'folder' ? folder : null} onPick={(id) => nav({ folder: id })} />
            </ul>
          )}
          {data ? <p className="muted small docs-usage">{tn('docs.usage', data.totals.files, { size: size(data.totals.bytes) })}</p> : null}
        </div>
      </nav>

      <section
        className={`panel docs-main${drag ? ' dragging' : ''}`}
        onDragOver={(e) => {
          if (!canDrop || !e.dataTransfer.types.includes('Files')) return;
          e.preventDefault();
          setDrag(true);
        }}
        onDragLeave={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node)) setDrag(false);
        }}
        onDrop={(e) => {
          if (!canDrop) return;
          e.preventDefault();
          setDrag(false);
          add(e.dataTransfer.files);
        }}
      >
        {view === 'folder' && crumbs.length ? (
          <nav className="tk-crumbs" aria-label={t('docs.crumbs')}>
            <button className="link" onClick={() => nav({ folder: null })}>
              {t('docs.all_folders')}
            </button>
            {crumbs.map((c, i) => (
              <span key={c.id} className="docs-crumb">
                <Icon name="chevronRight" size={14} />
                {i === crumbs.length - 1 ? (
                  <span>{c.name}</span>
                ) : (
                  <button className="link" onClick={() => nav({ folder: c.id })}>
                    {c.name}
                  </button>
                )}
              </span>
            ))}
          </nav>
        ) : null}
        <div className="panel-head docs-head">
          <div className="docs-title">
            <h2>
              {title}
              {current?.access === 'managers' ? <StatusBadge tone="warn" icon="lock" label={t('docs.managers_only')} /> : null}
            </h2>
            {view === 'folder' && current?.description ? <p className="muted small">{current.description}</p> : null}
            {view === 'folder' && !folder ? <p className="muted small">{t('docs.unfiled_hint')}</p> : null}
            {inTrash ? <p className="muted small">{t('docs.trash_hint')}</p> : null}
            {view === 'shared' ? <p className="muted small">{t('docs.shared_hint')}</p> : null}
          </div>
          <div className="docs-actions">
            {view === 'folder' ? (
              <>
                <input ref={picker} type="file" multiple hidden onChange={(e) => (e.target.files ? add(e.target.files) : null, (e.target.value = ''))} />
                <Button variant="primary" icon="upload" busy={!!uploading} onClick={() => picker.current?.click()}>
                  {uploading ? t('docs.uploading', { done: uploading.done, total: uploading.total }) : t('docs.upload')}
                </Button>
                {data?.can_manage ? (
                  <Button variant="secondary" icon="folder" onClick={() => setFolderForm({ parent: folder })}>
                    {folder ? t('docs.new_subfolder') : t('docs.new_folder')}
                  </Button>
                ) : null}
                {folder && data?.can_manage ? <IconButton icon="edit" label={t('docs.edit_folder')} onClick={() => setFolderForm({ id: folder, parent: current?.parent ?? null })} /> : null}
                {folder && data?.can_manage ? <IconButton icon="trash" label={t('docs.remove_folder')} onClick={() => setConfirm('remove_folder')} /> : null}
              </>
            ) : null}
            {inTrash && data?.can_manage && data.files.length ? (
              <Button variant="danger" icon="trash" onClick={() => setConfirm('empty')}>
                {t('docs.empty_trash')}
              </Button>
            ) : null}
          </div>
        </div>
        <div className="docs-tools">
          <label className="search">
            <Icon name="search" size={16} />
            <input
              className="input"
              type="search"
              value={q}
              placeholder={view === 'folder' ? t('docs.search_all') : t('docs.search')}
              aria-label={t('docs.search')}
              onChange={(e) => setQ(e.target.value)}
            />
          </label>
          {data?.tags.length && !inTrash ? (
            <div className="docs-tags" role="group" aria-label={t('docs.tags')}>
              {data.tags.slice(0, 12).map((x) => (
                <button key={x} className={`chip${tag === x ? ' active' : ''}`} aria-pressed={tag === x} onClick={() => setTag(tag === x ? null : x)}>
                  {x}
                </button>
              ))}
            </div>
          ) : null}
        </div>
        {selected.size ? (
          <div className="docs-bulk" role="region" aria-label={t('docs.selected_actions')}>
            <strong>{tn('docs.n_selected', selected.size)}</strong>
            {inTrash ? (
              <>
                <Button
                  size="sm"
                  icon="undo"
                  busy={busy}
                  onClick={() => void run(async () => (await action('doc.restore', { ids }), setSelected(new Set()), await reload()), tn('docs.restored', ids.length))}
                >
                  {t('docs.restore')}
                </Button>
                {data?.can_manage ? (
                  <Button size="sm" variant="danger" icon="trash" onClick={() => setConfirm('delete')}>
                    {t('docs.delete_forever')}
                  </Button>
                ) : null}
              </>
            ) : (
              <>
                <Button size="sm" icon="move" onClick={() => setMoving(ids)}>
                  {t('docs.move')}
                </Button>
                <Button size="sm" icon="download" busy={busy} onClick={() => void run(async () => saveFile(await action<{ name: string; mime: string; data: string }>('doc.zip', { ids })))}>
                  {t('docs.download_zip')}
                </Button>
                <Button size="sm" variant="ghost" icon="trash" onClick={() => setConfirm('trash')}>
                  {t('docs.trash')}
                </Button>
              </>
            )}
            <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
              {t('docs.clear_selection')}
            </Button>
          </div>
        ) : null}
        {drag ? (
          <div className="docs-drop" aria-hidden="true">
            <Icon name="upload" size={28} />
            <strong>{t('docs.drop_here', { folder: title })}</strong>
          </div>
        ) : null}
        {!data ? (
          <Skeleton h={260} />
        ) : !data.files.length && !(view === 'folder' && !searching && children.length) ? (
          <EmptyState
            icon={inTrash ? 'trash' : searching ? 'search' : 'folder'}
            title={
              inTrash ? t('docs.empty_trash_title') : searching ? t('docs.no_match') : view === 'shared' ? t('docs.none_shared') : view === 'recent' ? t('docs.none_recent') : t('docs.empty_folder')
            }
            body={view === 'folder' && !searching ? t('docs.empty_folder_hint') : undefined}
            action={
              view === 'folder' && !searching ? (
                <Button variant="primary" icon="upload" onClick={() => picker.current?.click()}>
                  {t('docs.upload')}
                </Button>
              ) : undefined
            }
          />
        ) : (
          <Table
            head={['', t('docs.name'), ...(view !== 'folder' || searching ? [t('docs.folder')] : []), inTrash ? t('docs.trashed') : t('docs.changed'), t('docs.size')]}
            left={view !== 'folder' || searching ? [1, 2] : [1]}
            phoneHide={view !== 'folder' || searching ? [2, 3, 4] : [2, 3]}
            rows={[
              ...(view === 'folder' && !searching
                ? children.map((c) => ({
                    key: `f-${c.id}`,
                    onClick: () => nav({ folder: c.id }),
                    cells: [
                      <span key="x" className="docs-check-gap" />,
                      <span key="n" className="docs-name">
                        <span className="doc-icon doc-folder" aria-hidden="true">
                          <Icon name="folder" size={20} />
                        </span>
                        <span>
                          <strong>{c.name}</strong>
                          <span className="muted small">
                            {[c.access === 'managers' ? t('docs.managers_only') : null, tn('docs.n_files', c.files), c.subfolders ? tn('docs.n_folders', c.subfolders) : null]
                              .filter(Boolean)
                              .join(' · ')}
                          </span>
                        </span>
                      </span>,
                      '',
                      '',
                    ],
                  }))
                : []),
              ...data.files.map((f) => ({
                key: f.id,
                onClick: () => setOpen(f.id),
                cells: [
                  <input
                    key="c"
                    type="checkbox"
                    aria-label={t('docs.select', { name: f.name })}
                    checked={selected.has(f.id)}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) =>
                      setSelected((s) => {
                        const n = new Set(s);
                        if (e.target.checked) n.add(f.id);
                        else n.delete(f.id);
                        return n;
                      })
                    }
                  />,
                  <span key="n" className="docs-name">
                    <FileIcon kind={f.kind} />
                    <span>
                      <strong>{f.name}</strong>
                      <span className="docs-meta">
                        {f.version > 1 ? <span className="docs-pill">v{f.version}</span> : null}
                        {f.shared ? (
                          <span className="docs-pill tone-blue">
                            <Icon name="link" size={12} /> {t('docs.shared')}
                          </span>
                        ) : null}
                        {f.source === 'sign' ? (
                          <span className="docs-pill tone-green">
                            <Icon name="pen" size={12} /> {t('docs.signed')}
                          </span>
                        ) : null}
                        {f.tags.slice(0, 3).map((x) => (
                          <span key={x} className="docs-pill">
                            {x}
                          </span>
                        ))}
                      </span>
                    </span>
                  </span>,
                  ...(view !== 'folder' || searching ? [f.folder_name ?? t('docs.unfiled')] : []),
                  <span key="d" className="docs-when">
                    {dateTime(inTrash ? f.trashed_at! : f.updated_at)}
                    <span className="muted small">{f.uploaded_by_name ?? ''}</span>
                  </span>,
                  size(f.size),
                ],
              })),
            ]}
          />
        )}
        {data?.files.length ? (
          <label className="docs-all">
            <input type="checkbox" checked={allChecked} onChange={(e) => setSelected(e.target.checked ? new Set(data.files.map((f) => f.id)) : new Set())} />
            <span className="small">{t('docs.select_all')}</span>
          </label>
        ) : null}
        {canDrop && data ? <p className="muted small docs-drop-hint">{t('docs.drop_hint')}</p> : null}
      </section>

      {open ? (
        <FileDialog
          id={open}
          folders={data?.folders ?? []}
          onClose={() => setOpen(null)}
          onChange={reload}
          onSign={(id) => {
            setOpen(null);
            onSign(id);
          }}
        />
      ) : null}
      {folderForm && data ? (
        <FolderForm
          id={folderForm.id ?? null}
          parent={folderForm.parent}
          folders={data.folders}
          onClose={() => setFolderForm(null)}
          onSaved={async (id) => {
            setFolderForm(null);
            await reload();
            if (!folderForm.id) nav({ folder: id });
          }}
        />
      ) : null}
      {moving && data ? (
        <MoveDialog
          ids={moving}
          folders={data.folders}
          from={folder}
          onClose={() => setMoving(null)}
          onMoved={async () => {
            setMoving(null);
            setSelected(new Set());
            await reload();
          }}
        />
      ) : null}
      {asking ? (
        <Dialog
          title={t('docs.exists_title')}
          size="sm"
          onClose={() => void resolve('skip')}
          footer={
            <>
              <Button variant="ghost" onClick={() => void resolve('skip')}>
                {t('docs.skip')}
              </Button>
              <span className="foot-actions">
                <Button variant="secondary" onClick={() => void resolve('copy')}>
                  {t('docs.keep_both')}
                </Button>
                <Button variant="primary" icon="layers" onClick={() => void resolve('version')}>
                  {t('docs.as_version')}
                </Button>
              </span>
            </>
          }
        >
          <p>{t('docs.exists_body', { name: asking.file.name })}</p>
          <p className="muted small">{t('docs.exists_hint')}</p>
        </Dialog>
      ) : null}
      {confirm ? (
        <Confirm
          title={
            confirm === 'trash'
              ? t('docs.trash_title')
              : confirm === 'delete'
                ? t('docs.delete_title')
                : confirm === 'empty'
                  ? t('docs.empty_title')
                  : t('docs.remove_folder_title', { name: current?.name ?? '' })
          }
          body={
            confirm === 'trash'
              ? tn('docs.trash_body', ids.length)
              : confirm === 'delete'
                ? tn('docs.delete_body', ids.length)
                : confirm === 'empty'
                  ? tn('docs.empty_body', data?.files.length ?? 0)
                  : t('docs.remove_folder_body')
          }
          confirm={confirm === 'trash' ? t('docs.trash') : confirm === 'remove_folder' ? t('docs.remove_folder') : t('docs.delete_forever')}
          danger={confirm !== 'trash'}
          busy={busy}
          onClose={() => setConfirm(null)}
          onConfirm={() =>
            void run(
              async () => {
                if (confirm === 'trash') await action('doc.trash', { ids });
                else if (confirm === 'delete') await action('doc.delete_forever', { ids });
                else if (confirm === 'empty') await action('doc.delete_forever', { all: true });
                else {
                  await action('doc.remove_folder', { id: folder });
                  nav({ folder: current?.parent ?? null });
                }
                setConfirm(null);
                setSelected(new Set());
                await reload();
              },
              confirm === 'trash' ? tn('docs.trashed_n', ids.length) : confirm === 'remove_folder' ? t('docs.folder_removed') : t('docs.deleted'),
            )
          }
        />
      ) : null}
    </div>
  );
}

function Tree({ folders, parent, depth, active, onPick }: { folders: FolderRow[]; parent: string | null; depth: number; active: string | null; onPick: (id: string) => void }) {
  const list = folders.filter((f) => f.parent === parent);
  if (!list.length) return null;
  return (
    <>
      {list.map((f) => (
        <li key={f.id} role="treeitem" aria-selected={active === f.id} aria-level={depth + 1}>
          <button className={active === f.id ? 'active' : ''} style={{ paddingInlineStart: `calc(var(--space-2) + ${depth} * var(--space-4))` }} onClick={() => onPick(f.id)}>
            <Icon name="folder" size={16} />
            <span>{f.name}</span>
            {f.access === 'managers' ? <Icon name="lock" size={12} /> : null}
            {f.files ? <span className="docs-count">{f.files}</span> : null}
          </button>
          <ul role="group">
            <Tree folders={folders} parent={f.id} depth={depth + 1} active={active} onPick={onPick} />
          </ul>
        </li>
      ))}
    </>
  );
}

/** The folders as a flat list with their path, for pickers. */
function folderOptions(folders: FolderRow[], except?: string | null) {
  const byId = new Map(folders.map((f) => [f.id, f]));
  const pathOf = (f: FolderRow) => {
    const parts: string[] = [];
    for (let x: FolderRow | undefined = f, n = 0; x && n < 50; x = x.parent ? byId.get(x.parent) : undefined, n++) parts.unshift(x.name);
    return parts.join(' › ');
  };
  const inside = (f: FolderRow): boolean => {
    for (let x: FolderRow | undefined = f, n = 0; x && n < 50; x = x.parent ? byId.get(x.parent) : undefined, n++) if (x.id === except) return true;
    return false;
  };
  return folders
    .filter((f) => !except || !inside(f))
    .map((f) => ({ id: f.id, label: pathOf(f), access: f.access }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

function FolderForm({ id, parent, folders, onClose, onSaved }: { id: string | null; parent: string | null; folders: FolderRow[]; onClose: () => void; onSaved: (id: string) => Promise<void> }) {
  const old = id ? folders.find((f) => f.id === id) : null;
  const [f, setF] = useState({ name: old?.name ?? '', parent: old ? old.parent : parent, access: old?.access ?? 'everyone', description: old?.description ?? '' });
  const { busy, run } = useSave();
  const options = folderOptions(folders, id);
  const parentAccess = f.parent ? folders.find((x) => x.id === f.parent)?.access : null;
  return (
    <Dialog
      title={id ? t('docs.edit_folder') : t('docs.new_folder')}
      size="sm"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            icon="check"
            busy={busy}
            disabled={!f.name.trim()}
            onClick={() =>
              void run(async () => {
                const r = await action<{ id: string }>('doc.save_folder', { ...(id ? { id } : {}), name: f.name, parent: f.parent, access: f.access, description: f.description.trim() || null });
                await onSaved(r.id);
              }, t('docs.folder_saved'))
            }
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <Field label={t('docs.folder_name')} required>
          <input className="input" value={f.name} maxLength={80} autoFocus onChange={(e) => setF({ ...f, name: e.target.value })} />
        </Field>
        <Field label={t('docs.inside')}>
          <select className="input" value={f.parent ?? ''} onChange={(e) => setF({ ...f, parent: e.target.value || null })}>
            <option value="">{t('docs.top_level')}</option>
            {options.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>
        <fieldset className="docs-access">
          <legend className="field-label">{t('docs.who_sees')}</legend>
          {(['everyone', 'managers'] as const).map((a) => (
            <button key={a} type="button" className={`option-card${f.access === a ? ' selected' : ''}`} aria-pressed={f.access === a} onClick={() => setF({ ...f, access: a })}>
              <Icon name={a === 'everyone' ? 'users' : 'lock'} size={18} />
              <strong>{t(`docs.access_${a}` as MessageKey)}</strong>
              <span className="muted small">{t(`docs.access_${a}_hint` as MessageKey)}</span>
            </button>
          ))}
        </fieldset>
        {parentAccess === 'managers' && f.access === 'everyone' ? <p className="callout callout-info small">{t('docs.inherits_managers')}</p> : null}
        <Field label={t('docs.description')} hint={t('docs.optional')}>
          <textarea className="input" rows={2} maxLength={500} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
        </Field>
      </div>
    </Dialog>
  );
}

function MoveDialog({ ids, folders, from, onClose, onMoved }: { ids: string[]; folders: FolderRow[]; from: string | null; onClose: () => void; onMoved: () => Promise<void> }) {
  const [to, setTo] = useState<string | null | undefined>(undefined);
  const { busy, run } = useSave();
  const options = folderOptions(folders);
  return (
    <Dialog
      title={tn('docs.move_title', ids.length)}
      size="sm"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            icon="move"
            busy={busy}
            disabled={to === undefined || to === from}
            onClick={() => void run(async () => (await action('doc.move', { ids, folder: to }), await onMoved()), tn('docs.moved', ids.length))}
          >
            {t('docs.move_here')}
          </Button>
        </>
      }
    >
      <ul className="docs-pick" role="listbox" aria-label={t('docs.folders')}>
        {[{ id: null as string | null, label: t('docs.unfiled'), access: 'everyone' as const }, ...options].map((o) => (
          <li key={o.id ?? 'none'}>
            <button role="option" aria-selected={to === o.id} className={to === o.id ? 'active' : ''} disabled={o.id === from} onClick={() => setTo(o.id)}>
              <Icon name="folder" size={16} />
              <span>{o.label}</span>
              {o.access === 'managers' ? <Icon name="lock" size={12} /> : null}
              {o.id === from ? <span className="muted small">{t('docs.here_now')}</span> : null}
            </button>
          </li>
        ))}
      </ul>
    </Dialog>
  );
}

/** A file: preview, details, tags and note, versions, links, history. */
function FileDialog({ id, folders, onClose, onChange, onSign }: { id: string; folders: FolderRow[]; onClose: () => void; onChange: () => Promise<void>; onSign: (id: string) => void }) {
  const { data: f, reload } = useAction<FileDetail>('doc.file', { id });
  const { toast, fail } = useUi();
  const { tenant } = useSession();
  const { busy, run } = useSave();
  const [preview, setPreview] = useState<{ url: string; text?: string } | null>(null);
  const [rename, setRename] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [tagText, setTagText] = useState('');
  const [moving, setMoving] = useState(false);
  const [shareDay, setShareDay] = useState('');
  const [sharing, setSharing] = useState(false);
  const [trashAsk, setTrashAsk] = useState(false);
  const versionInput = useRef<HTMLInputElement>(null);

  const loadPreview = async (detail: FileDetail) => {
    if (!['pdf', 'image', 'text'].includes(detail.kind) || detail.size > MAX_FILE) return;
    try {
      const d = await action<{ name: string; mime: string; data: string }>('doc.download', { id: detail.id });
      const blob = toBlob(d);
      const url = URL.createObjectURL(blob);
      setPreview({ url, text: detail.kind === 'text' ? (await blob.text()).slice(0, 200_000) : undefined });
    } catch {
      setPreview(null);
    }
  };
  useEffect(() => {
    if (f && !preview) void loadPreview(f);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f?.id, f?.version]);
  useEffect(() => () => (preview ? URL.revokeObjectURL(preview.url) : undefined), [preview]);

  const changed = async () => {
    await reload();
    await onChange();
  };
  const linkOf = (token: string) => `${window.location.origin}/f/${tenant?.slug ?? ''}/${token}`;
  const saveTags = (tags: string[]) => void run(async () => (await action('doc.update_file', { id, tags }), await changed()));
  const inTrash = !!f?.trashed_at;

  return (
    <Dialog
      title={f?.name ?? t('docs.file')}
      size="xl"
      onClose={onClose}
      footer={
        f ? (
          <>
            {inTrash ? (
              f.may_change ? (
                <Button variant="ghost" icon="undo" busy={busy} onClick={() => void run(async () => (await action('doc.restore', { ids: [id] }), await changed()), tn('docs.restored', 1))}>
                  {t('docs.restore')}
                </Button>
              ) : (
                <span />
              )
            ) : f.may_change ? (
              <Button variant="ghost" icon="trash" onClick={() => setTrashAsk(true)}>
                {t('docs.trash')}
              </Button>
            ) : (
              <span />
            )}
            <span className="foot-actions">
              {!inTrash && f.kind === 'pdf' && f.can_sign && f.source === 'upload' ? (
                <Button variant="secondary" icon="pen" onClick={() => onSign(f.id)}>
                  {t('docs.send_to_sign')}
                </Button>
              ) : null}
              <Button variant="primary" icon="download" busy={busy} onClick={() => void run(async () => saveFile(await action('doc.download', { id })))}>
                {t('docs.download')}
              </Button>
            </span>
          </>
        ) : undefined
      }
    >
      {!f ? (
        <Skeleton h={360} />
      ) : (
        <div className="docs-file">
          <div className="docs-preview">
            {preview && f.kind === 'pdf' ? (
              <>
                <iframe title={f.name} src={`${preview.url}#view=FitH`} />
                <a className="btn btn-ghost btn-sm docs-open" href={preview.url} target="_blank" rel="noopener">
                  <Icon name="eye" size={16} /> {t('docs.open_new_tab')}
                </a>
              </>
            ) : preview && f.kind === 'image' ? (
              <img src={preview.url} alt={f.name} />
            ) : preview?.text !== undefined ? (
              <pre>{preview.text}</pre>
            ) : (
              <div className="docs-nopreview">
                <FileIcon kind={f.kind} size={40} />
                <strong>{f.name}</strong>
                <span className="muted small">{['pdf', 'image', 'text'].includes(f.kind) ? t('docs.loading_preview') : t('docs.no_preview')}</span>
              </div>
            )}
          </div>
          <div className="docs-info">
            {inTrash ? <p className="callout callout-warn small">{t('docs.in_trash_since', { at: dateTime(f.trashed_at!), who: f.trashed_by_name ?? '—' })}</p> : null}
            <dl className="docs-facts">
              <dt>{t('docs.name')}</dt>
              <dd>
                {rename !== null ? (
                  <span className="docs-inline-edit">
                    <input className="input" value={rename} maxLength={200} autoFocus onChange={(e) => setRename(e.target.value)} aria-label={t('docs.new_name')} />
                    <IconButton
                      icon="check"
                      label={t('common.save')}
                      onClick={() =>
                        void run(async () => {
                          await action('doc.update_file', { id, name: rename });
                          setRename(null);
                          await changed();
                        }, t('docs.renamed'))
                      }
                    />
                    <IconButton icon="x" label={t('common.cancel')} onClick={() => setRename(null)} />
                  </span>
                ) : (
                  <span className="docs-inline-edit">
                    <span className="docs-break">{f.name}</span>
                    {f.may_change && !inTrash ? <IconButton icon="edit" label={t('docs.rename')} onClick={() => setRename(f.name)} /> : null}
                  </span>
                )}
              </dd>
              <dt>{t('docs.folder')}</dt>
              <dd>
                <span className="docs-inline-edit">
                  <span>{f.path.length ? f.path.join(' › ') : t('docs.unfiled')}</span>
                  {f.may_change && !inTrash ? <IconButton icon="move" label={t('docs.move')} onClick={() => setMoving(true)} /> : null}
                </span>
              </dd>
              <dt>{t('docs.size')}</dt>
              <dd>
                {size(f.size)} · {t(`docs.kind_${f.kind}` as MessageKey)}
              </dd>
              <dt>{t('docs.added')}</dt>
              <dd>
                {dateTime(f.created_at)} · {f.uploaded_by_name ?? '—'}
              </dd>
              <dt>{t('docs.fingerprint')}</dt>
              <dd>
                <code className="docs-hash" title={f.sha256}>
                  {f.sha256.slice(0, 20)}…
                </code>
              </dd>
            </dl>

            <section className="docs-sec">
              <h3>{t('docs.tags')}</h3>
              <div className="docs-tag-edit">
                {f.tags.map((x) => (
                  <span key={x} className="chip">
                    {x}
                    {!inTrash ? (
                      <button className="chip-x" aria-label={t('docs.remove_tag', { tag: x })} onClick={() => saveTags(f.tags.filter((y) => y !== x))}>
                        <Icon name="x" size={12} />
                      </button>
                    ) : null}
                  </span>
                ))}
                {!inTrash && f.tags.length < 12 ? (
                  <input
                    className="input docs-tag-input"
                    value={tagText}
                    maxLength={30}
                    placeholder={t('docs.add_tag')}
                    aria-label={t('docs.add_tag')}
                    onChange={(e) => setTagText(e.target.value)}
                    onKeyDown={(e) => {
                      if ((e.key === 'Enter' || e.key === ',') && tagText.trim()) {
                        e.preventDefault();
                        const tags = [...new Set([...f.tags, tagText.trim().toLowerCase()])];
                        setTagText('');
                        saveTags(tags);
                      }
                    }}
                  />
                ) : null}
              </div>
            </section>

            <section className="docs-sec">
              <h3>{t('docs.note')}</h3>
              {note !== null ? (
                <div className="form-stack">
                  <textarea className="input" rows={3} maxLength={1000} value={note} autoFocus onChange={(e) => setNote(e.target.value)} aria-label={t('docs.note')} />
                  <span className="docs-row-actions">
                    <Button size="sm" variant="ghost" onClick={() => setNote(null)}>
                      {t('common.cancel')}
                    </Button>
                    <Button
                      size="sm"
                      variant="primary"
                      busy={busy}
                      onClick={() =>
                        void run(async () => {
                          await action('doc.update_file', { id, note: note.trim() || null });
                          setNote(null);
                          await changed();
                        })
                      }
                    >
                      {t('common.save')}
                    </Button>
                  </span>
                </div>
              ) : (
                <button className="docs-note" disabled={inTrash} onClick={() => setNote(f.note ?? '')}>
                  {f.note ? <span className="fs-note">{f.note}</span> : <span className="muted">{t('docs.add_note')}</span>}
                </button>
              )}
            </section>

            {!inTrash ? (
              <section className="docs-sec">
                <div className="docs-sec-head">
                  <h3>{t('docs.share_links')}</h3>
                </div>
                {f.shares.length ? (
                  <ul className="docs-links">
                    {f.shares.map((s) => (
                      <li key={s.id} className={s.expired ? 'is-expired' : ''}>
                        <Icon name="link" size={16} />
                        <span className="docs-link-main">
                          <code className="docs-break">{linkOf(s.token)}</code>
                          <span className="muted small">
                            {[
                              s.expires ? (s.expired ? t('docs.link_expired', { day: dayText(s.expires) }) : t('docs.link_until', { day: dayText(s.expires) })) : t('docs.link_no_end'),
                              tn('docs.downloads', s.downloads),
                              s.by,
                            ]
                              .filter(Boolean)
                              .join(' · ')}
                          </span>
                        </span>
                        <IconButton
                          icon="copy"
                          label={t('docs.copy_link')}
                          onClick={() => void copy(linkOf(s.token)).then((ok) => toast(ok ? t('docs.copied') : t('docs.copy_failed'), ok ? 'ok' : 'danger'))}
                        />
                        <IconButton icon="x" label={t('docs.close_link')} onClick={() => void run(async () => (await action('doc.unshare', { id: s.id }), await changed()), t('docs.link_closed'))} />
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="muted small">{t('docs.no_links')}</p>
                )}
                {sharing ? (
                  <div className="docs-share-new">
                    <Field label={t('docs.link_last_day')} hint={t('docs.link_last_day_hint')}>
                      <input className="input" type="date" min={todayIso()} value={shareDay} onChange={(e) => setShareDay(e.target.value)} />
                    </Field>
                    <span className="docs-row-actions">
                      <Button size="sm" variant="ghost" onClick={() => setSharing(false)}>
                        {t('common.cancel')}
                      </Button>
                      <Button
                        size="sm"
                        variant="primary"
                        icon="link"
                        busy={busy}
                        onClick={() =>
                          void run(async () => {
                            const r = await action<{ token: string }>('doc.share', { id, expires: shareDay || null });
                            const ok = await copy(linkOf(r.token));
                            setSharing(false);
                            setShareDay('');
                            await changed();
                            toast(ok ? t('docs.link_made_copied') : t('docs.link_made'));
                          })
                        }
                      >
                        {t('docs.make_link')}
                      </Button>
                    </span>
                  </div>
                ) : (
                  <Button size="sm" variant="secondary" icon="share" onClick={() => setSharing(true)}>
                    {t('docs.new_link')}
                  </Button>
                )}
              </section>
            ) : null}

            <section className="docs-sec">
              <div className="docs-sec-head">
                <h3>{t('docs.versions')}</h3>
                {!inTrash && f.source === 'upload' ? (
                  <>
                    <input
                      ref={versionInput}
                      type="file"
                      hidden
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        e.target.value = '';
                        if (!file) return;
                        if (file.size > MAX_FILE) return fail(new ApiError(400, 'bad_request', t('docs.too_big', { name: file.name })));
                        void run(async () => {
                          await upload('doc.new_version', { id, name: file.name, data: await readBase64(file) });
                          setPreview(null);
                          await changed();
                        }, t('docs.version_added'));
                      }}
                    />
                    <Button size="sm" variant="ghost" icon="upload" onClick={() => versionInput.current?.click()}>
                      {t('docs.new_version')}
                    </Button>
                  </>
                ) : null}
              </div>
              <ul className="docs-versions">
                <li className="is-current">
                  <span className="docs-pill tone-blue">v{f.version}</span>
                  <span className="docs-link-main">
                    <span>{t('docs.current_version')}</span>
                    <span className="muted small">
                      {dateTime(f.updated_at)} · {f.uploaded_by_name ?? '—'}
                    </span>
                  </span>
                </li>
                {f.versions.map((v) => (
                  <li key={v.id}>
                    <span className="docs-pill">v{v.version}</span>
                    <span className="docs-link-main">
                      <span className="docs-break">{v.name}</span>
                      <span className="muted small">
                        {dateTime(v.at)} · {v.by ?? '—'} · {size(v.size)}
                      </span>
                    </span>
                    <IconButton
                      icon="download"
                      label={t('docs.download_version', { v: v.version })}
                      onClick={() => void run(async () => saveFile(await action('doc.download', { id, version: v.id })))}
                    />
                    {!inTrash && f.source === 'upload' ? (
                      <IconButton
                        icon="undo"
                        label={t('docs.restore_version', { v: v.version })}
                        onClick={() =>
                          void run(
                            async () => {
                              await action('doc.restore_version', { id, version: v.id });
                              setPreview(null);
                              await changed();
                            },
                            t('docs.version_restored', { v: v.version }),
                          )
                        }
                      />
                    ) : null}
                  </li>
                ))}
              </ul>
            </section>

            {f.signatures.length ? (
              <section className="docs-sec">
                <h3>{t('docs.signatures')}</h3>
                <ul className="docs-versions">
                  {f.signatures.map((s) => (
                    <li key={s.id}>
                      <Icon name="pen" size={16} />
                      <span className="docs-link-main">
                        <span>
                          {s.name} · {s.title}
                        </span>
                      </span>
                      <StatusBadge tone={SIGN_TONE[s.state].tone} icon={SIGN_TONE[s.state].icon} label={t(`docs.st_${s.state}` as MessageKey)} />
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            <details className="docs-sec docs-history">
              <summary>{t('docs.history')}</summary>
              <ul className="docs-timeline">
                {f.activity.map((a, i) => (
                  <li key={i}>
                    <span>{activityText(a)}</span>
                    <span className="muted small">
                      {dateTime(a.at)}
                      {a.by ? ` · ${a.by}` : ''}
                    </span>
                  </li>
                ))}
              </ul>
            </details>
          </div>
        </div>
      )}
      {moving && f ? (
        <MoveDialog
          ids={[f.id]}
          folders={folders}
          from={f.folder}
          onClose={() => setMoving(false)}
          onMoved={async () => {
            setMoving(false);
            await changed();
          }}
        />
      ) : null}
      {trashAsk && f ? (
        <Confirm
          title={t('docs.trash_title')}
          body={t('docs.trash_one', { name: f.name })}
          confirm={t('docs.trash')}
          busy={busy}
          onClose={() => setTrashAsk(false)}
          onConfirm={() =>
            void run(
              async () => {
                await action('doc.trash', { ids: [f.id] });
                await onChange();
                onClose();
              },
              tn('docs.trashed_n', 1),
            )
          }
        />
      ) : null}
    </Dialog>
  );
}

// ---- signatures ----------------------------------------------------------------------------

type SignFilter = 'waiting' | 'draft' | 'done' | 'closed' | 'all';

function SignTab({ onNew, onChange }: { onNew: () => void; onChange: () => Promise<void> }) {
  const [filter, setFilter] = useState<SignFilter>('waiting');
  const [q, setQ] = useState('');
  const { data, reload } = useAction<{ requests: RequestRow[]; counts: Record<SignFilter, number> }>('sign.requests', { filter, q: q.trim() || undefined });
  const [open, setOpen] = useState<string | null>(null);
  const filters: SignFilter[] = ['waiting', 'draft', 'done', 'closed', 'all'];
  return (
    <section className="panel">
      <div className="panel-head docs-sign-head">
        <div className="segmented" role="tablist" aria-label={t('docs.sign_show')}>
          {filters.map((x) => (
            <button key={x} role="tab" aria-selected={filter === x} className={filter === x ? 'active' : ''} onClick={() => setFilter(x)}>
              {t(`docs.f_${x}` as MessageKey)}
              {data?.counts[x] && x !== 'all' ? <span className="docs-count">{data.counts[x]}</span> : null}
            </button>
          ))}
        </div>
        <label className="search">
          <Icon name="search" size={16} />
          <input className="input" type="search" value={q} placeholder={t('docs.sign_search')} aria-label={t('docs.sign_search')} onChange={(e) => setQ(e.target.value)} />
        </label>
      </div>
      {!data ? (
        <Skeleton h={220} />
      ) : !data.requests.length ? (
        <EmptyState
          icon="pen"
          title={q ? t('docs.no_match') : t(`docs.sign_none_${filter}` as MessageKey)}
          body={filter === 'waiting' && !q ? t('docs.sign_none_hint') : undefined}
          action={
            filter === 'waiting' && !q ? (
              <Button variant="primary" icon="pen" onClick={onNew}>
                {t('docs.new_request')}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <ul className="docs-requests">
          {data.requests.map((r) => (
            <li key={r.id}>
              <button className="docs-request" onClick={() => setOpen(r.id)}>
                <span className="docs-request-main">
                  <span className="docs-request-title">
                    <strong>{r.title}</strong>
                    <StatusBadge tone={SIGN_TONE[r.state].tone} icon={SIGN_TONE[r.state].icon} label={t(`docs.st_${r.state}` as MessageKey)} />
                  </span>
                  <span className="muted small">{[r.name, r.doc_name, r.by].filter(Boolean).join(' · ')}</span>
                  <span className="docs-signers">
                    {r.signers.map((s, i) => (
                      <span key={i} className={`docs-signer is-${s.state}`} title={t(`docs.ss_${s.state}` as MessageKey)}>
                        <Icon name={SIGNER_TONE[s.state].icon} size={12} />
                        {s.name}
                      </span>
                    ))}
                  </span>
                </span>
                <span className="docs-request-side">
                  {r.signers.length ? (
                    <span className="docs-progress-label">
                      {t('docs.n_of_m_signed', { n: r.signed, m: r.signers.length })}
                      <span className="tk-progress" aria-hidden="true">
                        <span style={{ width: `${Math.round((r.signed / r.signers.length) * 100)}%` }} />
                      </span>
                    </span>
                  ) : null}
                  <span className="muted small">
                    {r.state === 'completed' && r.completed_at
                      ? t('docs.completed_on', { at: dateTime(r.completed_at) })
                      : r.state === 'sent' && r.expires
                        ? t('docs.sign_by', { day: dayText(r.expires) })
                        : r.sent_at
                          ? t('docs.sent_on', { at: dateTime(r.sent_at) })
                          : t('docs.made_on', { at: dateTime(r.created_at) })}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {open ? (
        <RequestDialog
          id={open}
          onClose={() => setOpen(null)}
          onChange={async () => {
            await reload();
            await onChange();
          }}
          onClosed={async () => {
            setOpen(null);
            await reload();
            await onChange();
          }}
        />
      ) : null}
    </section>
  );
}

interface SignerDraft {
  key: number;
  name: string;
  email: string;
  phone: string;
}

function RequestForm({ id, file, onClose, onSaved }: { id?: string; file: string | null; onClose: () => void; onSaved: () => Promise<void> }) {
  const { data: old } = useAction<RequestDetail | null>(id ? 'sign.request' : 'sign.pdfs', id ? { id } : {});
  const { data: choices } = useAction<{
    files: { id: string; name: string; size: number; folder_name: string | null; updated_at: string }[];
    folders: { id: string; name: string; parent: string | null }[];
  }>('sign.pdfs');
  const { me } = useSession();
  const { busy, run } = useSave();
  const seq = useRef(1);
  const [f, setF] = useState({ title: '', message: '', ordered: true, expires: '', file: file as string | null });
  const [signers, setSigners] = useState<SignerDraft[]>([{ key: 0, name: '', email: '', phone: '' }]);
  const [pdf, setPdf] = useState<File | null>(null);
  const [folder, setFolder] = useState<string | null>(null);
  const [pick, setPick] = useState('');
  const [tried, setTried] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const loaded = useRef(false);
  useEffect(() => {
    if (!id || loaded.current || !old || !('signers' in old)) return;
    loaded.current = true;
    const r = old as RequestDetail;
    setF({ title: r.title, message: r.message ?? '', ordered: r.ordered, expires: r.expires ?? '', file: r.file });
    setSigners(r.signers.length ? r.signers.map((s) => ({ key: seq.current++, name: s.name, email: s.email ?? '', phone: s.phone ?? '' })) : signers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [old]);
  useEffect(() => {
    if (!f.title && f.file && choices) {
      const c = choices.files.find((x) => x.id === f.file);
      if (c) setF((x) => ({ ...x, title: x.title || c.name.replace(/\.pdf$/i, '') }));
    }
  }, [choices, f.file, f.title]);

  const chosen = choices?.files.find((x) => x.id === f.file) ?? null;
  const list = (choices?.files ?? []).filter((x) => !pick.trim() || x.name.toLowerCase().includes(pick.trim().toLowerCase())).slice(0, 8);
  const named = signers.filter((s) => s.name.trim());
  const emailBad = (s: SignerDraft) => !!s.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.email.trim());
  const problems = {
    title: f.title.trim().length < 2,
    doc: !f.file && !pdf,
    signers: !named.length || signers.some((s) => (s.name.trim() && s.name.trim().length < 2) || emailBad(s)),
  };
  const setSigner = (key: number, patch: Partial<SignerDraft>) => setSigners((l) => l.map((s) => (s.key === key ? { ...s, ...patch } : s)));
  const moveSigner = (i: number, d: -1 | 1) =>
    setSigners((l) => {
      const n = [...l];
      const [x] = n.splice(i, 1);
      n.splice(i + d, 0, x!);
      return n;
    });
  const save = (send: boolean) => {
    setTried(true);
    if (problems.title || (send && (problems.doc || problems.signers)) || signers.some(emailBad)) return;
    void run(
      async () => {
        const body = {
          ...(id ? { id } : {}),
          title: f.title.trim(),
          message: f.message.trim() || null,
          ordered: f.ordered,
          expires: f.expires || null,
          file: pdf ? null : f.file,
          upload: pdf ? { name: pdf.name, data: await readBase64(pdf), folder } : null,
          signers: named.map((s) => ({ name: s.name.trim(), email: s.email.trim() || null, phone: s.phone.trim() || null })),
        };
        const r = await upload<{ id: string }>('sign.save', body);
        if (send) await action('sign.send', { id: r.id });
        await onSaved();
      },
      send ? t('docs.request_sent') : t('docs.draft_saved'),
    );
  };
  return (
    <Dialog
      title={id ? t('docs.edit_request') : t('docs.new_request')}
      size="lg"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <span className="foot-actions">
            <Button variant="secondary" icon="edit" busy={busy} onClick={() => save(false)}>
              {t('docs.save_draft')}
            </Button>
            <Button variant="primary" icon="send" busy={busy} onClick={() => save(true)}>
              {t('docs.save_send')}
            </Button>
          </span>
        </>
      }
    >
      <div className="form-stack">
        <Field label={t('docs.request_title')} error={tried && problems.title ? t('docs.e_title') : null} required>
          <input className="input" value={f.title} maxLength={160} placeholder={t('docs.request_title_ph')} onChange={(e) => setF({ ...f, title: e.target.value })} />
        </Field>

        <fieldset className="docs-fieldset">
          <legend className="field-label">{t('docs.document_to_sign')}</legend>
          {pdf ? (
            <div className="docs-chosen">
              <FileIcon kind="pdf" />
              <span className="docs-link-main">
                <strong className="docs-break">{pdf.name}</strong>
                <span className="muted small">
                  {size(pdf.size)} · {t('docs.new_upload')}
                </span>
              </span>
              <IconButton icon="x" label={t('docs.choose_other')} onClick={() => setPdf(null)} />
            </div>
          ) : chosen ? (
            <div className="docs-chosen">
              <FileIcon kind="pdf" />
              <span className="docs-link-main">
                <strong className="docs-break">{chosen.name}</strong>
                <span className="muted small">
                  {chosen.folder_name ?? t('docs.unfiled')} · {size(chosen.size)}
                </span>
              </span>
              <IconButton icon="x" label={t('docs.choose_other')} onClick={() => setF({ ...f, file: null })} />
            </div>
          ) : (
            <div className="docs-picker">
              <label className="search">
                <Icon name="search" size={16} />
                <input className="input" type="search" value={pick} placeholder={t('docs.find_pdf')} aria-label={t('docs.find_pdf')} onChange={(e) => setPick(e.target.value)} />
              </label>
              {!choices ? (
                <Skeleton h={80} />
              ) : list.length ? (
                <ul className="docs-pick" role="listbox" aria-label={t('docs.pdfs')}>
                  {list.map((x) => (
                    <li key={x.id}>
                      <button role="option" aria-selected={false} onClick={() => setF({ ...f, file: x.id, title: f.title || x.name.replace(/\.pdf$/i, '') })}>
                        <FileIcon kind="pdf" size={16} />
                        <span className="docs-break">{x.name}</span>
                        <span className="muted small">{x.folder_name ?? t('docs.unfiled')}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="muted small">{t('docs.no_pdfs')}</p>
              )}
              <div className="docs-or">
                <span className="muted small">{t('docs.or')}</span>
                <input
                  ref={input}
                  type="file"
                  accept="application/pdf"
                  hidden
                  onChange={(e) => {
                    const x = e.target.files?.[0] ?? null;
                    e.target.value = '';
                    if (x) {
                      setPdf(x);
                      if (!f.title) setF({ ...f, title: x.name.replace(/\.pdf$/i, '') });
                    }
                  }}
                />
                <Button size="sm" variant="secondary" icon="upload" onClick={() => input.current?.click()}>
                  {t('docs.upload_pdf')}
                </Button>
              </div>
            </div>
          )}
          {pdf ? (
            <Field label={t('docs.keep_in')}>
              <select className="input" value={folder ?? ''} onChange={(e) => setFolder(e.target.value || null)}>
                <option value="">{t('docs.unfiled')}</option>
                {(choices?.folders ?? [])
                  .map((x) => ({ ...x, label: x.name }))
                  .sort((a, b) => a.label.localeCompare(b.label))
                  .map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.label}
                    </option>
                  ))}
              </select>
            </Field>
          ) : null}
          {tried && problems.doc ? (
            <p className="form-error" role="alert">
              <Icon name="alert" size={14} /> {t('docs.e_doc')}
            </p>
          ) : null}
          {pdf && pdf.size > MAX_FILE ? <p className="form-error">{t('docs.too_big', { name: pdf.name })}</p> : null}
        </fieldset>

        <fieldset className="docs-fieldset">
          <legend className="field-label">{t('docs.who_signs')}</legend>
          <ol className="docs-signer-rows">
            {signers.map((s, i) => (
              <li key={s.key}>
                <span className="esign-step" aria-hidden="true">
                  {i + 1}
                </span>
                <div className="docs-signer-fields">
                  <input
                    className="input"
                    value={s.name}
                    maxLength={100}
                    placeholder={t('docs.signer_name')}
                    aria-label={t('docs.signer_name_n', { n: i + 1 })}
                    onChange={(e) => setSigner(s.key, { name: e.target.value })}
                  />
                  <input
                    className={`input${emailBad(s) ? ' invalid' : ''}`}
                    type="email"
                    value={s.email}
                    maxLength={160}
                    placeholder={t('docs.signer_email')}
                    aria-label={t('docs.signer_email_n', { n: i + 1 })}
                    aria-invalid={emailBad(s)}
                    onChange={(e) => setSigner(s.key, { email: e.target.value })}
                  />
                  <input
                    className="input"
                    type="tel"
                    value={s.phone}
                    maxLength={40}
                    placeholder={t('docs.signer_phone')}
                    aria-label={t('docs.signer_phone_n', { n: i + 1 })}
                    onChange={(e) => setSigner(s.key, { phone: e.target.value })}
                  />
                </div>
                <span className="docs-signer-tools">
                  <IconButton icon="up" label={t('docs.move_up')} disabled={i === 0} onClick={() => moveSigner(i, -1)} />
                  <IconButton icon="down" label={t('docs.move_down')} disabled={i === signers.length - 1} onClick={() => moveSigner(i, 1)} />
                  <IconButton icon="x" label={t('docs.remove_signer')} disabled={signers.length === 1} onClick={() => setSigners((l) => l.filter((x) => x.key !== s.key))} />
                </span>
              </li>
            ))}
          </ol>
          <div className="docs-row-actions start">
            <Button size="sm" variant="ghost" icon="plus" disabled={signers.length >= 10} onClick={() => setSigners((l) => [...l, { key: seq.current++, name: '', email: '', phone: '' }])}>
              {t('docs.add_signer')}
            </Button>
            {me?.name && !signers.some((s) => s.name.trim() === me.name) ? (
              <Button
                size="sm"
                variant="ghost"
                icon="users"
                disabled={signers.length >= 10}
                onClick={() =>
                  setSigners((l) => {
                    const empty = l.findIndex((x) => !x.name.trim());
                    if (empty >= 0) return l.map((x, i) => (i === empty ? { ...x, name: me.name } : x));
                    return [...l, { key: seq.current++, name: me.name, email: '', phone: '' }];
                  })
                }
              >
                {t('docs.add_me')}
              </Button>
            ) : null}
          </div>
          {tried && problems.signers ? (
            <p className="form-error" role="alert">
              <Icon name="alert" size={14} /> {t('docs.e_signers')}
            </p>
          ) : null}
          {signers.length > 1 ? <Switch checked={f.ordered} onChange={(v) => setF({ ...f, ordered: v })} label={f.ordered ? t('docs.in_order') : t('docs.any_order')} /> : null}
        </fieldset>

        <div className="form-grid">
          <Field label={t('docs.last_day')} hint={t('docs.optional')}>
            <input className="input" type="date" min={todayIso()} value={f.expires} onChange={(e) => setF({ ...f, expires: e.target.value })} />
          </Field>
        </div>
        <Field label={t('docs.message')} hint={t('docs.message_hint')}>
          <textarea className="input" rows={3} maxLength={2000} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} />
        </Field>
        <p className="muted small">{t('docs.ses_note')}</p>
      </div>
    </Dialog>
  );
}

function RequestDialog({ id, onClose, onChange, onClosed }: { id: string; onClose: () => void; onChange: () => Promise<void>; onClosed: () => Promise<void> }) {
  const { data: r, reload } = useAction<RequestDetail>('sign.request', { id });
  const { tenant } = useSession();
  const { toast } = useUi();
  const { busy, run } = useSave();
  const [cancelling, setCancelling] = useState(false);
  const [reason, setReason] = useState('');
  const [editing, setEditing] = useState(false);
  const [removing, setRemoving] = useState(false);
  const linkOf = (token: string) => `${window.location.origin}/sign/${tenant?.slug ?? ''}/${token}`;
  const invite = (s: RequestDetail['signers'][number]) => t('docs.invite_text', { name: s.name, title: r!.title, link: linkOf(s.token!) });
  const changed = async () => {
    await reload();
    await onChange();
  };
  if (editing && r)
    return (
      <RequestForm
        id={r.id}
        file={r.file}
        onClose={() => setEditing(false)}
        onSaved={async () => {
          setEditing(false);
          await changed();
        }}
      />
    );
  const turn = r?.state === 'sent' ? r.signers.find((s) => s.state === 'ready' || s.state === 'viewed') : null;
  return (
    <Dialog
      title={r ? `${r.name} · ${r.title}` : t('docs.request')}
      size="lg"
      onClose={onClose}
      footer={
        r ? (
          <>
            {r.state === 'draft' && r.may_change ? (
              <Button variant="ghost" icon="trash" onClick={() => setRemoving(true)}>
                {t('docs.delete_draft')}
              </Button>
            ) : r.state === 'sent' && r.may_change ? (
              <Button variant="ghost" icon="x" onClick={() => setCancelling(true)}>
                {t('docs.cancel_request')}
              </Button>
            ) : (
              <span />
            )}
            <span className="foot-actions">
              <Button variant="secondary" icon="download" busy={busy} onClick={() => void run(async () => saveFile(await action('sign.download', { id, what: 'document' })))}>
                {t('docs.download_document')}
              </Button>
              {r.state === 'completed' ? (
                <Button variant="primary" icon="shieldCheck" busy={busy} onClick={() => void run(async () => saveFile(await action('sign.download', { id, what: 'certificate' })))}>
                  {t('docs.download_certificate')}
                </Button>
              ) : r.state === 'draft' && r.may_change ? (
                <>
                  <Button variant="secondary" icon="edit" onClick={() => setEditing(true)}>
                    {t('docs.edit')}
                  </Button>
                  <Button variant="primary" icon="send" busy={busy} onClick={() => void run(async () => (await action('sign.send', { id }), await changed()), t('docs.request_sent'))}>
                    {t('docs.send_now')}
                  </Button>
                </>
              ) : null}
            </span>
          </>
        ) : undefined
      }
    >
      {!r ? (
        <Skeleton h={300} />
      ) : (
        <div className="form-stack docs-request-detail">
          <div className="docs-status-line">
            <StatusBadge tone={SIGN_TONE[r.state].tone} icon={SIGN_TONE[r.state].icon} label={t(`docs.st_${r.state}` as MessageKey)} />
            <span className="muted small">
              {[
                r.by ? t('docs.by', { who: r.by }) : null,
                r.sent_at ? t('docs.sent_on', { at: dateTime(r.sent_at) }) : null,
                r.expires ? t('docs.sign_by', { day: dayText(r.expires) }) : null,
                r.ordered && r.signers.length > 1 ? t('docs.in_order_short') : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </span>
          </div>
          {r.state === 'sent' && turn ? (
            <p className="callout callout-info">
              <Icon name="clock" size={16} /> {r.ordered ? t('docs.waiting_for', { name: turn.name }) : t('docs.waiting_for_any', { n: r.signers.filter((s) => s.state !== 'signed').length })}
            </p>
          ) : null}
          {r.closed_reason ? (
            <p className="callout callout-warn">
              <Icon name="alert" size={16} /> {r.closed_reason}
            </p>
          ) : null}
          {r.state === 'completed' ? (
            <p className="callout callout-ok">
              <Icon name="checkCircle" size={16} /> {t('docs.completed_filed')}
            </p>
          ) : null}
          <div className="docs-chosen">
            <FileIcon kind="pdf" />
            <span className="docs-link-main">
              <strong className="docs-break">{r.doc_name ?? r.file_name ?? t('docs.no_document')}</strong>
              <span className="muted small">
                {r.doc_sha256 ? (
                  <>
                    {t('docs.fingerprint')} <code title={r.doc_sha256}>{r.doc_sha256.slice(0, 16)}…</code>
                    {r.doc_size ? ` · ${size(r.doc_size)}` : ''}
                  </>
                ) : (
                  t('docs.frozen_on_send')
                )}
              </span>
            </span>
          </div>
          {r.message ? <p className="fs-note">{r.message}</p> : null}

          <h3 className="docs-h3">{t('docs.signers')}</h3>
          <ol className="docs-signer-list">
            {r.signers.map((s) => (
              <li key={s.id} className={`is-${s.state}`}>
                <span className="esign-step" aria-hidden="true">
                  {s.state === 'signed' ? <Icon name="check" size={14} /> : s.state === 'declined' ? <Icon name="x" size={14} /> : s.position}
                </span>
                <div className="docs-signer-body">
                  <div className="docs-signer-top">
                    <strong>{s.name}</strong>
                    <StatusBadge tone={SIGNER_TONE[s.state].tone} icon={SIGNER_TONE[s.state].icon} label={t(`docs.ss_${s.state}` as MessageKey)} />
                  </div>
                  <span className="muted small">{[s.email, s.phone].filter(Boolean).join(' · ') || t('docs.no_contact')}</span>
                  {s.state === 'signed' ? (
                    <div className="docs-signed">
                      {s.signature ? <SignatureView value={s.signature} label={t('docs.signature_of', { name: s.name })} /> : null}
                      <span className="muted small">
                        {t('docs.signed_as', { name: s.signed_name ?? s.name, at: dateTime(s.signed_at!) })}
                        {s.ip ? ` · IP ${s.ip}` : ''}
                      </span>
                    </div>
                  ) : s.state === 'declined' ? (
                    <span className="small tk-warn">{t('docs.declined_because', { reason: s.decline_reason ?? '' })}</span>
                  ) : s.token && r.state === 'sent' ? (
                    <div className="docs-invite">
                      {s.viewed_at ? <span className="muted small">{t('docs.opened_at', { at: dateTime(s.viewed_at) })}</span> : null}
                      <div className="docs-invite-actions">
                        <Button
                          size="sm"
                          variant="secondary"
                          icon="copy"
                          onClick={() => void copy(linkOf(s.token!)).then((ok) => toast(ok ? t('docs.copied') : t('docs.copy_failed'), ok ? 'ok' : 'danger'))}
                        >
                          {t('docs.copy_link')}
                        </Button>
                        {s.email ? (
                          <a
                            className="btn btn-ghost btn-sm"
                            href={`mailto:${encodeURIComponent(s.email)}?subject=${encodeURIComponent(t('docs.invite_subject', { title: r.title }))}&body=${encodeURIComponent(invite(s))}`}
                          >
                            <Icon name="mail" size={16} /> {t('docs.email')}
                          </a>
                        ) : null}
                        <a
                          className="btn btn-ghost btn-sm"
                          href={`https://wa.me/${(s.phone ?? '').replace(/[^0-9]/g, '')}?text=${encodeURIComponent(invite(s))}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Icon name="send" size={16} /> {t('docs.whatsapp')}
                        </a>
                      </div>
                      {s.state === 'waiting' ? <span className="muted small">{t('docs.after_previous')}</span> : null}
                    </div>
                  ) : null}
                </div>
              </li>
            ))}
          </ol>

          {r.events.length ? (
            <details className="docs-history">
              <summary>{t('docs.history')}</summary>
              <ul className="docs-timeline">
                {r.events.map((e, i) => (
                  <li key={i}>
                    <span>
                      {t(`docs.ev_${e.kind}` as MessageKey)}
                      {e.who ? ` · ${e.who}` : ''}
                      {e.kind === 'declined' || e.kind === 'cancelled' ? ` · ${e.text ?? ''}` : ''}
                    </span>
                    <span className="muted small">
                      {dateTime(e.at)}
                      {e.ip ? ` · IP ${e.ip}` : ''}
                    </span>
                  </li>
                ))}
              </ul>
            </details>
          ) : null}
        </div>
      )}
      {cancelling ? (
        <Dialog
          title={t('docs.cancel_title')}
          size="sm"
          onClose={() => setCancelling(false)}
          footer={
            <>
              <Button variant="ghost" onClick={() => setCancelling(false)}>
                {t('docs.keep_waiting')}
              </Button>
              <Button
                variant="danger"
                busy={busy}
                disabled={reason.trim().length < 3}
                onClick={() =>
                  void run(async () => {
                    await action('sign.cancel', { id, reason: reason.trim() });
                    setCancelling(false);
                    await changed();
                  }, t('docs.cancelled'))
                }
              >
                {t('docs.cancel_request')}
              </Button>
            </>
          }
        >
          <div className="form-stack">
            <p className="muted">{t('docs.cancel_body')}</p>
            <Field label={t('docs.cancel_reason')} required>
              <textarea className="input" rows={2} maxLength={300} value={reason} onChange={(e) => setReason(e.target.value)} />
            </Field>
          </div>
        </Dialog>
      ) : null}
      {removing ? (
        <Confirm
          title={t('docs.delete_draft')}
          body={t('docs.delete_draft_body')}
          confirm={t('docs.delete_draft')}
          danger
          busy={busy}
          onClose={() => setRemoving(false)}
          onConfirm={() => void run(async () => (await action('sign.delete_draft', { id }), await onClosed()), t('docs.draft_deleted'))}
        />
      ) : null}
    </Dialog>
  );
}
